/**
 * Skill-zone motion policy — pad muls, zone presence, AA-steal suppress.
 * Data: `balance.combat.skillZone.<skillId>`. Phaser-free.
 * Soft→AA / speedMul / leap.phase / landDamage 금지.
 */
import { raidCatalog } from "../../data/raid-catalog";
import type { RaidUnit } from "./raid-types";
import { engagementStyle } from "./raid-engage";
import {
  pointInDangerZone,
  type RaidDangerZone,
} from "./raid-threat";

type ZoneShape = "circle" | "cone" | "line";

type SkillZoneRow = {
  shape?: ZoneShape;
  multiFloorPadMul?: number;
  multiFloorApproachMul?: number;
  soloPadMul?: number;
  soloRadialOutBias?: number;
  keepClearPadMul?: number;
  midHpKeepClearHpPct?: number;
  suppressAaGoalSteal?: number | boolean;
  adjutantSpreadFloor?: number;
  /** Ally soft-sep: mute impulse toward active leap land (rim re-entry). */
  softSepInwardMute?: number | boolean;
};

const DEFAULT_SHAPE: Record<string, ZoneShape> = {
  leap: "circle",
  charge: "line",
  brass: "cone",
};

function skillZoneTable(): Record<string, SkillZoneRow> {
  const raw = (raidCatalog.balance.combat as { skillZone?: unknown }).skillZone;
  if (raw && typeof raw === "object") return raw as Record<string, SkillZoneRow>;
  return {};
}

export function skillZoneRow(skillId: string): SkillZoneRow {
  return skillZoneTable()[skillId] ?? {};
}

export function skillZoneShape(skillId: string): ZoneShape {
  const s = skillZoneRow(skillId).shape;
  if (s === "circle" || s === "cone" || s === "line") return s;
  return DEFAULT_SHAPE[skillId] ?? "circle";
}

function finitePos(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : fallback;
}

function flagOn(v: unknown): boolean {
  return v === 1 || v === true;
}

export function skillZoneMultiFloorPadMul(skillId: string): number {
  return finitePos(skillZoneRow(skillId).multiFloorPadMul, 1.75);
}

export function skillZoneMultiFloorApproachMul(skillId: string): number {
  return finitePos(skillZoneRow(skillId).multiFloorApproachMul, 1.85);
}

export function skillZoneSoloPadMul(skillId: string): number {
  return finitePos(skillZoneRow(skillId).soloPadMul, 1.9);
}

export function skillZoneSoloRadialOutBias(skillId: string): number {
  const v = skillZoneRow(skillId).soloRadialOutBias;
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return 0;
  return Math.min(1, v);
}

export function skillZoneKeepClearPadMul(skillId: string): number {
  return finitePos(skillZoneRow(skillId).keepClearPadMul, 1.45);
}

export function skillZoneMidHpKeepClearHpPct(skillId: string): number {
  const v = skillZoneRow(skillId).midHpKeepClearHpPct;
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return 0.55;
  return Math.min(1, v);
}

export function skillZoneSuppressAaGoalSteal(skillId: string): boolean {
  return flagOn(skillZoneRow(skillId).suppressAaGoalSteal);
}

export function skillZoneAdjutantSpreadFloor(skillId: string): number {
  return finitePos(skillZoneRow(skillId).adjutantSpreadFloor, 0.95);
}

/** Leap-disk soft-sep: strip inward radial impulse (multi-kill rim leak). */
export function skillZoneSoftSepInwardMute(skillId: string): boolean {
  return flagOn(skillZoneRow(skillId).softSepInwardMute);
}

/**
 * Remove soft-sep impulse toward leap land center.
 * Zone-scoped only — does not inflate Soft pad / pair dist / landDamage.
 */
export function muteInwardLeapSepImpulse(
  x: number,
  y: number,
  sx: number,
  sy: number,
  landX: number,
  landY: number,
): { x: number; y: number } {
  const dx = x - landX;
  const dy = y - landY;
  const d = Math.hypot(dx, dy);
  if (d < 1e-6) return { x: sx, y: sy };
  const rx = dx / d;
  const ry = dy / d;
  const radial = rx * sx + ry * sy;
  if (radial >= 0) return { x: sx, y: sy };
  return { x: sx - rx * radial, y: sy - ry * radial };
}

/** Zone matches table shape + skillId (skill foresight floors). */
export function zonesHaveSkillShape(
  zones: readonly RaidDangerZone[],
  skillId: string,
): boolean {
  const shape = skillZoneShape(skillId);
  for (const z of zones) {
    if (z.shape === shape && z.skillId === skillId) return true;
  }
  return false;
}

/** Leap circle + charge line both active (mid/late Colossus signature). */
export function zonesHaveLeapAndCharge(
  zones: readonly RaidDangerZone[],
): boolean {
  return zonesHaveSkillShape(zones, "leap") && zonesHaveSkillShape(zones, "charge");
}

/** Leap circle active with no charge line (post–판결 A remain signature). */
export function zonesHaveSoloLeap(zones: readonly RaidDangerZone[]): boolean {
  return zonesHaveSkillShape(zones, "leap") && !zonesHaveSkillShape(zones, "charge");
}

/** Brass cone telegraph/active floor present. */
export function zonesHaveBrass(zones: readonly RaidDangerZone[]): boolean {
  return zonesHaveSkillShape(zones, "brass");
}

function unitHpFrac(u: RaidUnit): number {
  return u.maxHp > 0 ? u.hp / u.maxHp : 1;
}

/** Kite/skirmish (ranged DPS/healer) — brass mid-HP keep-clear audience. */
function brassBacklineUnit(u: RaidUnit): boolean {
  const eng = engagementStyle(u.classId);
  return eng === "kite" || eng === "skirmish";
}

/**
 * Brass cone pad factor — **mid/low-HP backline only** (판결 B).
 * Tanks / full-HP ranged keep baseline pad.
 */
export function brassPadFactor(
  u: RaidUnit,
  z: Pick<RaidDangerZone, "skillId">,
): number {
  if (z.skillId !== "brass") return 1;
  if (!brassBacklineUnit(u)) return 1;
  if (unitHpFrac(u) > skillZoneMidHpKeepClearHpPct("brass")) return 1;
  return skillZoneKeepClearPadMul("brass");
}

/**
 * Soft engage clear pad — baseline 4px. Brass mid-HP keep-clear uses
 * escapeCone pad + adjutant brass spread (not Soft pad inflation).
 */
export function brassSoftClearPadPx(
  _u: RaidUnit,
  _zones: readonly RaidDangerZone[],
): number {
  return 4;
}

/** Mid-HP backline: true when brass keep-clear boosts should apply. */
export function brassMidHpKeepClearActive(
  u: RaidUnit,
  zones: readonly RaidDangerZone[],
): boolean {
  return (
    zonesHaveBrass(zones) &&
    brassBacklineUnit(u) &&
    unitHpFrac(u) <= skillZoneMidHpKeepClearHpPct("brass")
  );
}

function feetInSkillZone(
  u: RaidUnit,
  zones: readonly RaidDangerZone[],
  skillId: string,
): boolean {
  const shape = skillZoneShape(skillId);
  return zones.some(
    (z) =>
      z.shape === shape &&
      z.skillId === skillId &&
      pointInDangerZone(u.x, u.y, u.radius, z),
  );
}

/**
 * Shared: suppress pack-exit / AA goal steal when skill-zone policy says so.
 * Solo leap = zone presence; brass = mid-HP keep-clear + feet-in cone.
 */
export function suppressAaGoalStealForSkillZones(
  u: RaidUnit,
  zones: readonly RaidDangerZone[],
): boolean {
  if (skillZoneSuppressAaGoalSteal("leap") && zonesHaveSoloLeap(zones)) {
    return true;
  }
  if (
    skillZoneSuppressAaGoalSteal("brass") &&
    brassMidHpKeepClearActive(u, zones) &&
    feetInSkillZone(u, zones, "brass")
  ) {
    return true;
  }
  return false;
}

/** Leap pad mul for multi-floor vs solo (판결 A / A′). */
export function leapPadMulForZones(zones: readonly RaidDangerZone[]): number {
  if (zonesHaveLeapAndCharge(zones)) return skillZoneMultiFloorPadMul("leap");
  if (zonesHaveSoloLeap(zones)) return skillZoneSoloPadMul("leap");
  return 1;
}
